import { useState } from 'react';
import { Badge, Lightbox } from '@/components';
import { LabelValue, hasValue, FeatureBadge } from '../shared';
import type { AuctionRecord } from '@/types';

export function AuctionRecordCard({ record }: { record: AuctionRecord }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [techSpecsExpanded, setTechSpecsExpanded] = useState(true);

  const images = record.images || [];
  const specs = record['technical-specs'];
  const titleInfo = record['title-and-condition'];
  const saleInfo = record['sale-date-location'];
  const features = record['car-features'];
  const listingHistory = record['listing-history'];

  // Get price display
  const priceDisplay = hasValue(record.price) ? record.price : 'Price Not Available';
  const priceColor = hasValue(record.price) ? 'text-green-400' : 'text-gray-400';

  // Check if tech specs section has any displayable values
  const hasTechSpecs = specs && (
    hasValue(specs['Engine Type']) ||
    hasValue(specs.Cylinders) ||
    hasValue(specs['Fuel Type']) ||
    hasValue(specs.Transmission) ||
    hasValue(specs['Body Style']) ||
    hasValue(specs.Color) ||
    hasValue(specs.Drive) ||
    hasValue(specs['Estimated Repair Cost']) ||
    hasValue(specs['Avg. Estimated Retail Value']) ||
    hasValue(specs['Damage Ratio'])
  );

  // Check if auction info has any displayable values
  const hasAuctionInfo = saleInfo && (
    hasValue(saleInfo['Auction Date']) ||
    hasValue(saleInfo.Location) ||
    hasValue(saleInfo['Seller Type']) ||
    hasValue(saleInfo['Auction Type']) ||
    hasValue(saleInfo['Buyer Country'])
  ) || hasValue(record['lot-number']);

  // Check if listing history has entries
  const historyEntries = listingHistory ? Object.entries(listingHistory).filter(([, v]) => hasValue(v)) : [];

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="bg-obdai-deep rounded-xl overflow-hidden">
      {/* A. Header: Price + Status */}
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        <span className={`text-2xl font-bold ${priceColor}`}>{priceDisplay}</span>
        <Badge variant={record['sale status'] === 'Sold' ? 'success' : 'warning'}>
          {record['sale status'] || 'Unknown'}
        </Badge>
      </div>

      {/* B. Photo Gallery with Lightbox */}
      {images.length > 0 && (
        <div className="relative bg-black/30">
          <div className="flex gap-2 overflow-x-auto p-3">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => openLightbox(idx)}
                className="flex-shrink-0 rounded-lg overflow-hidden hover:ring-2 hover:ring-purple-400 transition-all"
              >
                <img
                  src={img}
                  alt={`Auction image ${idx + 1}`}
                  className="h-24 w-auto object-cover"
                />
              </button>
            ))}
          </div>
          <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded">
            {images.length} photos
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* C. Vehicle Summary */}
        {hasValue(record.vname) && (
          <p className="text-white font-semibold text-lg">{record.vname}</p>
        )}

        {/* D. Key Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {hasValue(specs?.Odometer) && (
            <LabelValue label="Odometer" value={specs!.Odometer} />
          )}
          {hasValue(titleInfo?.['Title Type']) && (
            <LabelValue label="Title Type" value={titleInfo!['Title Type']} />
          )}
          {hasValue(titleInfo?.['Primary Damage']) && (
            <LabelValue label="Primary Damage" value={titleInfo!['Primary Damage']} />
          )}
          {hasValue(titleInfo?.['Secondary Damage']) && (
            <LabelValue label="Secondary Damage" value={titleInfo!['Secondary Damage']} />
          )}
        </div>

        {/* E. Technical Specs (collapsible) */}
        {hasTechSpecs && (
          <div className="border-t border-white/10 pt-3">
            <button
              onClick={() => setTechSpecsExpanded(!techSpecsExpanded)}
              className="w-full flex items-center justify-between text-left mb-3"
            >
              <span className="text-xs text-gray-500 uppercase tracking-wide">Technical Specs</span>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${techSpecsExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {techSpecsExpanded && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {hasValue(specs?.['Engine Type']) && (
                  <LabelValue label="Engine" value={specs!['Engine Type']} />
                )}
                {hasValue(specs?.Cylinders) && (
                  <LabelValue label="Cylinders" value={specs!.Cylinders} />
                )}
                {hasValue(specs?.['Fuel Type']) && (
                  <LabelValue label="Fuel" value={specs!['Fuel Type']} />
                )}
                {hasValue(specs?.Transmission) && (
                  <LabelValue label="Transmission" value={specs!.Transmission} />
                )}
                {hasValue(specs?.['Body Style']) && (
                  <LabelValue label="Body Style" value={specs!['Body Style']} />
                )}
                {hasValue(specs?.Color) && (
                  <LabelValue label="Color" value={specs!.Color} />
                )}
                {hasValue(specs?.Drive) && (
                  <LabelValue label="Drivetrain" value={specs!.Drive} />
                )}
                {hasValue(specs?.['Estimated Repair Cost']) && (
                  <LabelValue label="Est. Repair Cost" value={specs!['Estimated Repair Cost']} />
                )}
                {hasValue(specs?.['Avg. Estimated Retail Value']) && (
                  <LabelValue label="Avg. Retail Value" value={specs!['Avg. Estimated Retail Value']} />
                )}
                {hasValue(specs?.['Damage Ratio']) && (
                  <LabelValue label="Damage Ratio" value={specs!['Damage Ratio']} />
                )}
              </div>
            )}
          </div>
        )}

        {/* F. Auction Info */}
        {hasAuctionInfo && (
          <div className="border-t border-white/10 pt-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Auction Information</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {hasValue(saleInfo?.['Auction Date']) && (
                <LabelValue label="Auction Date" value={saleInfo!['Auction Date']} />
              )}
              {hasValue(saleInfo?.Location) && (
                <LabelValue label="Location" value={saleInfo!.Location} />
              )}
              {hasValue(saleInfo?.['Seller Type']) && (
                <LabelValue label="Seller Type" value={saleInfo!['Seller Type']} />
              )}
              {hasValue(saleInfo?.['Auction Type']) && (
                <LabelValue label="Auction Type" value={saleInfo!['Auction Type']} />
              )}
              {hasValue(record['lot-number']) && (
                <LabelValue label="Lot Number" value={record['lot-number']} />
              )}
              {hasValue(saleInfo?.['Buyer Country']) && (
                <LabelValue label="Buyer Country" value={saleInfo!['Buyer Country']} />
              )}
            </div>
          </div>
        )}

        {/* G. Car Features Badges */}
        {features && (
          <div className="flex flex-wrap gap-2 border-t border-white/10 pt-3">
            <FeatureBadge label="Keys" value={features.Has_Keys} />
            <FeatureBadge label="Runs/Drives" value={features.Runs_Drives} />
            <FeatureBadge label="Engine Starts" value={features.Engine_Starts} />
          </div>
        )}

        {/* H. Listing History Timeline */}
        {historyEntries.length > 0 && (
          <div className="border-t border-white/10 pt-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Listing History</p>
            <div className="space-y-1">
              {historyEntries.map(([date, event]) => (
                <div key={date} className="flex items-center gap-3 text-sm">
                  <span className="text-gray-500 font-mono">{date}</span>
                  <span className="text-gray-300">{event}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
